import { useMemo } from 'react';
import {
	BigNum,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
} from '@velocity-exchange/sdk';
import {
	DRIFT_TOKEN_BANK_INDEX,
	DSOL_MARKET_INDEX,
	JLP_POOL_JLP_MARKET_INDEX,
} from '../environmentVariables/EnvironmentVariables';
import useBanksToRender from 'src/hooks/useBanksToRender';
import useHighlightedSpotMarkets from 'src/hooks/useHighlightedSpotMarkets';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import {
	MAIN_POOL_ID,
	JLP_POOL_ID,
	SACRED_POOL_ID,
} from '@velocity-exchange/common';
import { useIsolatedSpotToMainSpotMap } from 'src/hooks/spotMarkets/useIsolatedSpotToMainSpotMap';
import { useAllIfData } from 'src/hooks/query/ifData/useAllIfData';
import { useAmplifyStats } from 'src/hooks/useAmplifyStats';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import { useAllMarketInterestData } from 'src/hooks/earn/useAllMarketInterestData';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useAllVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import { getApyToUse } from 'src/utils/vault/math';
import { useAllWalletBalances } from 'src/hooks/query/walletBalances/useWalletBalance';
import {
	getProductTitle,
	getActionButtons,
	isRangeApy,
	isNew,
} from 'src/components/Earn/utils/earnProductUtils';
import {
	sortExploreProducts,
	isStableCoin,
	isSolAndLst,
} from 'src/components/Earn/ExploreAndCompare/sortExploreProducts';
import { DEFAULT_EXPLORE_SORT_CONFIG } from 'src/components/Earn/ExploreAndCompare/defaultExploreSortConfig';
import { useEarnConfig } from './useEarnConfig';

//TODO: find decent way to handle this

export type ProductType =
	| 'all'
	| 'borrow_lend'
	| 'strategy_vaults'
	| 'insurance_fund_vaults'
	| 'amplify'
	| 'd_sol_staking'
	| 'super_stake'
	| 'governance';
export type AssetFilter = 'all' | 'stable_coin' | 'sol_and_lst' | 'in_wallet';

const AMPLIFY_DEPOSIT_ASSETS = [JLP_POOL_JLP_MARKET_INDEX];

// Types
export type ActionButtonType =
	| 'deposit'
	| 'see_all'
	| 'details'
	| 'stake'
	| 'view_amplify'
	| 'super_stake'
	| 'stake_sol'
	| 'view_vaults';

export type ActionButtonConfig = {
	type: ActionButtonType;
	highlight?: boolean;
	mobileText?: string;
};

export type EarnProduct = {
	productType: ProductType;
	marketSymbol: string;
	marketIndex: number;
	title: string;
	apyToShow: number;
	lendApy?: number;
	promoApy?: number;
	nativeApy?: number;
	ifApy?: number;
	actionButtons: ActionButtonConfig[];
	apyInfoAction?: () => void;
	isRangeApy?: boolean;
	isNew?: boolean;
	totalDepositQuote?: BigNum;
	walletBalance?: {
		base: BigNum;
		quote: BigNum;
	};
};

export type EarnAccordionOptions = Pick<
	EarnProduct,
	| 'productType'
	| 'marketSymbol'
	| 'apyToShow'
	| 'totalDepositQuote'
	| 'walletBalance'
	| 'marketIndex'
	| 'title'
	| 'lendApy'
	| 'promoApy'
	| 'nativeApy'
	| 'ifApy'
	| 'actionButtons'
	| 'isRangeApy'
	| 'isNew'
>;

export type EarnProductsByAsset = {
	spotMarket: SpotMarketConfig;
	apyToShow: number;
	apyExcludingIfVaults: number;
	featured: boolean;
	isPromo: boolean;
	accordionOptions: EarnAccordionOptions[];
	highestApyProductType: ProductType;
	lstApy?: number;
	walletBalance?: {
		base: BigNum;
		quote: BigNum;
	};
	totalDepositQuote?: BigNum;
	isNew?: boolean;
};

export const isEarnProductByAsset = (
	product: EarnProductsByAsset | EarnAccordionOptions
): product is EarnProductsByAsset => {
	return 'accordionOptions' in product;
};

export const useEarnProduct = ({
	assetFilter,
	productFilter,
	searchInputValue,
}: {
	assetFilter: AssetFilter;
	productFilter: ProductType;
	searchInputValue: string;
}) => {
	const driftClientIsReady = useDriftClientIsReady();

	const userKey = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.userKey
	);
	const highlightedMarkets = useHighlightedSpotMarkets();
	const orderedBanks = useBanksToRender({
		sortBy: 'walletBalance',
		userKey,
		opts: {
			highlightBanks: highlightedMarkets,
		},
		poolId: MAIN_POOL_ID,
	});
	const { data: ifStakeData } = useAllIfData();
	const spotMarketData = useDriftStore((s) => s.borrowLendData);
	const isolatedSpotToMainSpotMap = useIsolatedSpotToMainSpotMap();
	const jlpAmplifyStats = useAmplifyStats(userKey ?? '', JLP_POOL_ID);
	const { data: vaultStatsData, isLoading: isVaultsLoading } =
		useAllVaultStats();
	const vaultsStatsList = vaultStatsData.list;
	const areInitialVaultsStatsLoaded = !isVaultsLoading;

	// dSOL
	const { data: allMarketData } = useAllMarketInterestData();
	const allMarketInterest = allMarketData ?? {};

	const allWalletBalances = useAllWalletBalances();
	const { data: earnConfig } = useEarnConfig();

	const getWalletBalanceUSDC = (spotMarketSymbol: string) => {
		return allWalletBalances.get(spotMarketSymbol);
	};

	// Build accordion options with complete data for components
	const buildAccordionOptions = (
		marketSymbol: string,
		spotMarket: SpotMarketConfig
	): EarnAccordionOptions[] => {
		const options: EarnAccordionOptions[] = [];

		// Helper function to create complete product data
		const createOption = (
			productType: ProductType,
			marketSymbol: string,
			marketIndex: number
		): EarnAccordionOptions => {
			const marketInterest = allMarketInterest[marketIndex];
			const title = getProductTitle(productType, marketIndex);
			const actionButtons = getActionButtons(productType);

			// Initialize default values
			let apyToShow = 0;
			let lendApy = 0;
			let promoApy = 0;
			let nativeApy = 0;
			let ifApy = 0;

			// Calculate data based on product type
			if (productType === 'borrow_lend' && marketInterest) {
				apyToShow = marketInterest.deposit.totalApy;
				lendApy = marketInterest.deposit.baseApy;
				promoApy = marketInterest.deposit.promoApy;
				nativeApy = marketInterest.deposit.nativeApy;
			} else if (
				productType === 'insurance_fund_vaults' ||
				productType === 'governance'
			) {
				// Use 7-day APY from store instead of nextApr
				const ifApyValue = ifStakeData[marketIndex]?.vault?.sevenDayApy ?? 0;
				const nativeApyValue = marketInterest?.deposit.nativeApy ?? 0;
				apyToShow = ifApyValue + nativeApyValue;
				ifApy = ifApyValue;
				nativeApy = nativeApyValue;
				lendApy = ifApyValue;
			} else if (productType === 'd_sol_staking') {
				const dSolMarketData = allMarketInterest[DSOL_MARKET_INDEX];
				apyToShow = dSolMarketData?.native?.apy ?? 0;
			} else if (productType === 'strategy_vaults') {
				if (areInitialVaultsStatsLoaded) {
					const allVaultsForSpotMarket = vaultsStatsList.filter(
						(vault) => vault.uiVaultConfig.depositAsset === marketIndex
					);
					const oldVaultsForSpotMarket = vaultsStatsList
						.filter(
							(vault) =>
								!vault.uiVaultConfig.isNew(vault.numOfVaultSnapshots ?? 0)
						)
						.filter(
							(vault) => vault.uiVaultConfig.depositAsset === marketIndex
						);

					if (allVaultsForSpotMarket.length > 0) {
						const vaultsListForHighestApy =
							oldVaultsForSpotMarket.length > 0
								? oldVaultsForSpotMarket
								: allVaultsForSpotMarket;

						apyToShow = Math.max(
							...vaultsListForHighestApy.map(
								(vault) =>
									getApyToUse(
										vault.uiVaultConfig,
										vault.apys['90d'],
										vault.numOfVaultSnapshots
									).apy
							)
						);
						lendApy = apyToShow;
					}
				}
			} else if (productType === 'amplify') {
				const amplifyStats = jlpAmplifyStats?.getAmplifyMarketStats()?.[0];
				apyToShow = amplifyStats?.maxApy ?? 0;
				lendApy = apyToShow;
			}

			return {
				productType,
				marketSymbol,
				marketIndex,
				title,
				actionButtons,
				apyToShow,
				lendApy,
				promoApy,
				nativeApy,
				ifApy,
				walletBalance: getWalletBalanceUSDC(marketSymbol),
				isRangeApy: isRangeApy(productType),
				isNew: isNew(marketIndex),
			};
		};

		// Lending product
		if (spotMarket) {
			options.push(
				createOption('borrow_lend', marketSymbol, spotMarket.marketIndex)
			);
		}

		// Add isolated pool products first
		const mainSpotMarketsToIsolatedSpotMarketsMap = Object.keys(
			isolatedSpotToMainSpotMap
		).reduce(
			(acc, isolatedSpotMarketIndex) => {
				const mainSpotMarketIndex =
					isolatedSpotToMainSpotMap[Number(isolatedSpotMarketIndex)];

				if (!acc[mainSpotMarketIndex]) {
					acc[mainSpotMarketIndex] = [];
				}

				acc[mainSpotMarketIndex].push(+isolatedSpotMarketIndex);

				return acc;
			},
			{} as Record<number, number[]>
		);

		const isolatedSpotCounterparts = mainSpotMarketsToIsolatedSpotMarketsMap[
			spotMarket.marketIndex
		] as number[] | undefined;

		// First add all isolated lending products to maintain original order
		if (isolatedSpotCounterparts?.length) {
			isolatedSpotCounterparts.forEach((isolatedMarketIndex) => {
				const isolatedMarketData = spotMarketData.find(
					(mkt) => mkt.bankConfig.marketIndex === isolatedMarketIndex
				);

				if (
					!isolatedMarketData ||
					isolatedMarketData.bankConfig.poolId === SACRED_POOL_ID
				)
					return;

				// Isolated lending
				options.push(
					createOption('borrow_lend', marketSymbol, isolatedMarketIndex)
				);
			});
		}

		// Insurance Fund products (Main Market first)
		if (spotMarket && spotMarket.marketIndex !== DRIFT_TOKEN_BANK_INDEX) {
			options.push(
				createOption(
					'insurance_fund_vaults',
					spotMarket.symbol,
					spotMarket.marketIndex
				)
			);
		}

		// Then add all isolated IF products
		if (isolatedSpotCounterparts?.length) {
			isolatedSpotCounterparts.forEach((isolatedMarketIndex) => {
				const isolatedMarketData = spotMarketData.find(
					(mkt) => mkt.bankConfig.marketIndex === isolatedMarketIndex
				);

				if (
					!isolatedMarketData ||
					isolatedMarketData.bankConfig.poolId === SACRED_POOL_ID
				)
					return;

				const isolatedSpotMarket = isolatedMarketData.bankConfig;

				// Isolated IF
				options.push(
					createOption(
						'insurance_fund_vaults',
						isolatedSpotMarket.symbol,
						isolatedMarketIndex
					)
				);

				// Amplify for JLP
				if (AMPLIFY_DEPOSIT_ASSETS.includes(isolatedMarketIndex)) {
					options.push(
						createOption(
							'amplify',
							isolatedSpotMarket.symbol,
							isolatedMarketIndex
						)
					);
				}
			});
		}

		// Governance
		if (spotMarket && spotMarket.marketIndex === DRIFT_TOKEN_BANK_INDEX) {
			options.push(
				createOption('governance', spotMarket.symbol, spotMarket.marketIndex)
			);
		}

		// dSOL staking
		if (spotMarket?.symbol === 'SOL') {
			options.push(
				createOption('d_sol_staking', 'SOL', spotMarket.marketIndex)
			);
		}

		// Strategy vaults (added after isolated products to maintain original order)
		if (spotMarket && areInitialVaultsStatsLoaded) {
			const allVaultsForSpotMarket = vaultsStatsList.filter(
				(vault) => vault.uiVaultConfig.depositAsset === spotMarket.marketIndex
			);

			if (allVaultsForSpotMarket.length > 0) {
				options.push(
					createOption(
						'strategy_vaults',
						spotMarket.symbol,
						spotMarket.marketIndex
					)
				);
			}
		}

		return options;
	};

	// Function that calculates APY for sorting at the asset level
	const getProductInfo = (
		marketSymbol: string
	): EarnProductsByAsset | undefined => {
		const marketData = spotMarketData.find(
			(mkt) =>
				mkt.bankConfig.symbol === marketSymbol &&
				mkt.bankConfig.poolId === MAIN_POOL_ID
		);

		let isPromo = false;

		const spotMarket = marketData?.bankConfig;
		if (!spotMarket) return undefined;

		// Calculate APYs for sorting
		const baseLendApy =
			allMarketInterest[spotMarket.marketIndex]?.deposit.baseApy ?? 0;
		const nativeApy =
			allMarketInterest[spotMarket.marketIndex]?.deposit.nativeApy ?? 0;
		const promoApy =
			allMarketInterest[spotMarket.marketIndex]?.deposit.promoApy ?? 0;

		if (promoApy > 0) {
			isPromo = true;
		}

		const lendApyPct = baseLendApy + promoApy + nativeApy;
		const ifApyPct =
			ifStakeData[spotMarket?.marketIndex]?.vault?.sevenDayApy ?? 0;

		// Track max APY and its product type efficiently
		let maxApy = Math.max(lendApyPct, ifApyPct + nativeApy);
		let maxApyProductType: ProductType =
			maxApy === lendApyPct ? 'borrow_lend' : 'insurance_fund_vaults';
		let maxApyExcludingIfVaults = lendApyPct;

		const accordionOptions = buildAccordionOptions(marketSymbol, spotMarket);
		const walletBalance = getWalletBalanceUSDC(spotMarket.symbol);

		// Calculate total deposit quote by asset (still needed for display)
		const mainSpotMarketsToIsolatedSpotMarketsMap = Object.keys(
			isolatedSpotToMainSpotMap
		).reduce(
			(acc, isolatedSpotMarketIndex) => {
				const mainSpotMarketIndex =
					isolatedSpotToMainSpotMap[Number(isolatedSpotMarketIndex)];

				if (!acc[mainSpotMarketIndex]) {
					acc[mainSpotMarketIndex] = [];
				}

				acc[mainSpotMarketIndex].push(+isolatedSpotMarketIndex);

				return acc;
			},
			{} as Record<number, number[]>
		);

		const isolatedSpotCounterparts = mainSpotMarketsToIsolatedSpotMarketsMap[
			spotMarket.marketIndex
		] as number[] | undefined;

		// Add isolated pool APYs to comparison
		if (isolatedSpotCounterparts?.length) {
			isolatedSpotCounterparts.forEach((isolatedMarketIndex) => {
				const isolatedMarketData = spotMarketData.find(
					(mkt) => mkt.bankConfig.marketIndex === isolatedMarketIndex
				);

				// Skip SACRED pool markets
				if (
					!isolatedMarketData ||
					isolatedMarketData.bankConfig.poolId === SACRED_POOL_ID
				)
					return;

				const isolatedBaseLendApy =
					allMarketInterest[isolatedMarketIndex]?.deposit.baseApy ?? 0;
				const isolatedPromoApy =
					allMarketInterest[isolatedMarketIndex]?.deposit.promoApy ?? 0;

				if (isolatedPromoApy > 0) {
					isPromo = true;
				}

				const isolatedLendApyPct =
					isolatedBaseLendApy + isolatedPromoApy + nativeApy;
				const isolatedIfApy =
					ifStakeData[isolatedMarketIndex]?.vault?.sevenDayApy ?? 0;

				// Update max APY tracking
				if (isolatedLendApyPct > maxApy) {
					maxApy = isolatedLendApyPct;
					maxApyProductType = 'borrow_lend';
				}
				if (isolatedIfApy + nativeApy > maxApy) {
					maxApy = isolatedIfApy + nativeApy;
					maxApyProductType = 'insurance_fund_vaults';
				}
				if (isolatedLendApyPct > maxApyExcludingIfVaults) {
					maxApyExcludingIfVaults = isolatedLendApyPct;
				}
			});
		}

		// Add strategy vaults APY
		if (areInitialVaultsStatsLoaded) {
			const allVaultsForSpotMarket = vaultsStatsList.filter(
				(vault) => vault.uiVaultConfig.depositAsset === spotMarket.marketIndex
			);
			const oldVaultsForSpotMarket = vaultsStatsList
				.filter(
					(vault) => !vault.uiVaultConfig.isNew(vault.numOfVaultSnapshots ?? 0)
				)
				.filter(
					(vault) => vault.uiVaultConfig.depositAsset === spotMarket.marketIndex
				);

			if (allVaultsForSpotMarket.length > 0) {
				const vaultsListForHighestApy =
					oldVaultsForSpotMarket.length > 0
						? oldVaultsForSpotMarket
						: allVaultsForSpotMarket;

				const highestRelevantVaultApy = Math.max(
					...vaultsListForHighestApy.map(
						(vault) =>
							getApyToUse(
								vault.uiVaultConfig,
								vault.apys['90d'],
								vault.numOfVaultSnapshots
							).apy
					)
				);

				// Update max APY tracking
				if (highestRelevantVaultApy > maxApy) {
					maxApy = highestRelevantVaultApy;
					maxApyProductType = 'strategy_vaults';
				}
				if (highestRelevantVaultApy > maxApyExcludingIfVaults) {
					maxApyExcludingIfVaults = highestRelevantVaultApy;
				}
			}
		}

		// Add dSOL APY for SOL
		if (spotMarket.symbol === 'SOL') {
			const dSolMarketData = allMarketInterest[DSOL_MARKET_INDEX];
			const dSolMetrics = dSolMarketData
				? [
						...dSolMarketData.deposit.breakdown,
						...dSolMarketData.borrow.breakdown,
					]
				: [];
			const dsolApy = dSolMetrics?.[0]?.apy ?? 0;

			// Update max APY tracking
			if (dsolApy > maxApy) {
				maxApy = dsolApy;
				maxApyProductType = 'd_sol_staking';
			}
			if (dsolApy > maxApyExcludingIfVaults) {
				maxApyExcludingIfVaults = dsolApy;
			}
		}

		// Add Amplify APY if applicable
		if (
			isolatedSpotCounterparts?.some((idx) =>
				AMPLIFY_DEPOSIT_ASSETS.includes(idx)
			)
		) {
			const amplifyStats = jlpAmplifyStats?.getAmplifyMarketStats()?.[0];
			if (amplifyStats?.maxApy) {
				const amplifyMaxApy = amplifyStats.maxApy;

				// Update max APY tracking
				if (amplifyMaxApy > maxApy) {
					maxApy = amplifyMaxApy;
					maxApyProductType = 'amplify';
				}
				if (amplifyMaxApy > maxApyExcludingIfVaults) {
					maxApyExcludingIfVaults = amplifyMaxApy;
				}
			}
		}

		const totalDepositQuoteByAsset = (
			marketData?.totalDepositsQuote ?? BigNum.zero(QUOTE_PRECISION_EXP)
		).add(
			(isolatedSpotCounterparts ?? [])?.reduce((sum, isolatedMarketIndex) => {
				const isolatedMarketData = spotMarketData.find(
					(mkt) => mkt.bankConfig.marketIndex === isolatedMarketIndex
				);
				if (!isolatedMarketData) return sum;
				return sum.add(
					isolatedMarketData.totalDepositsQuote ??
						BigNum.zero(QUOTE_PRECISION_EXP)
				);
			}, BigNum.zero(QUOTE_PRECISION_EXP)) ?? BigNum.zero(QUOTE_PRECISION_EXP)
		);

		return {
			spotMarket: spotMarket,
			apyToShow: maxApy,
			apyExcludingIfVaults: maxApyExcludingIfVaults,
			isPromo,
			featured: !!highlightedMarkets.find(
				(mkt) => mkt.marketIndex === spotMarket.marketIndex
			),
			lstApy: nativeApy,
			accordionOptions,
			highestApyProductType: maxApyProductType,
			walletBalance,
			totalDepositQuote: totalDepositQuoteByAsset,
			isNew: isNew(spotMarket.marketIndex),
		};
	};

	const earnProductsByAsset = useMemo(() => {
		// Must map market symbols to rows now because we can have two markets of the same symbol in different pools now
		const marketSymbols = new Set(
			orderedBanks.map((bank) => bank.market.symbol)
		);
		const products = [...marketSymbols].map((marketSymbol) =>
			getProductInfo(marketSymbol)
		);
		let filteredProducts = products.filter(Boolean) as EarnProductsByAsset[];

		// Filter by asset type
		filteredProducts = filteredProducts.filter((product) => {
			if (assetFilter === 'all') return true;
			if (
				assetFilter === 'stable_coin' &&
				isStableCoin(product.spotMarket.marketIndex)
			)
				return true;
			if (
				assetFilter === 'sol_and_lst' &&
				isSolAndLst(product.spotMarket.symbol)
			)
				return true;
			if (assetFilter === 'in_wallet') {
				return true;
			}
			return false;
		});

		// Filter by search input
		if (searchInputValue) {
			filteredProducts = filteredProducts.filter((product) =>
				product.spotMarket.symbol
					.toLowerCase()
					.includes(searchInputValue.toLowerCase())
			);
		}
		const walletBanks = orderedBanks.filter((bank) => {
			return bank.quoteValue >= 0.01;
		});
		const sortConfig =
			earnConfig?.explore_sortkeys ?? DEFAULT_EXPLORE_SORT_CONFIG;

		// Filter by assets in wallet with minimum dollar value
		if (assetFilter === 'in_wallet') {
			filteredProducts = filteredProducts.filter((product) =>
				walletBanks.some(
					(bank) => bank.market.symbol === product.spotMarket.symbol
				)
			);
		}

		// Note: We no longer limit the number of products here based on showAll
		// DriftEarnTable now handles that filtering itself

		return sortExploreProducts(filteredProducts, sortConfig);
	}, [
		driftClientIsReady,
		spotMarketData.length,
		orderedBanks,
		isolatedSpotToMainSpotMap,
		areInitialVaultsStatsLoaded,
		allMarketInterest,
		assetFilter,
		searchInputValue,
		ifStakeData,
	]);

	const earnProducts = useMemo(() => {
		const sortConfig =
			earnConfig?.explore_sortkeys ?? DEFAULT_EXPLORE_SORT_CONFIG;
		return sortExploreProducts(
			earnProductsByAsset
				.flatMap((product) => product.accordionOptions)
				.filter((product) => {
					if (productFilter === 'all') return true;
					return product.productType === productFilter;
				}),
			sortConfig
		);
	}, [earnProductsByAsset, earnConfig?.explore_sortkeys]);

	return productFilter === 'all' ? earnProductsByAsset : earnProducts;
};
